const DEFAULT_STALL_TIMEOUT_MS = 15_000
const MIN_EXTERNAL_FALLBACK_DELAY_MS = 500
const MEDIA_TIME_EPSILON = 0.000_001

export interface FrameSchedulerHealth {
  readonly running: boolean
  readonly processing: boolean
  readonly stalled: boolean
  readonly startedAt: number | null
  readonly lastVideoFrameAt: number | null
  readonly lastSuccessfulAt: number | null
}

export interface FrameSchedulerOptions {
  readonly stallTimeoutMs?: number
  readonly externalFallbackDelayMs?: number
  readonly initialMediaTime?: number | null
  readonly now?: () => number
  readonly onStalled?: (health: FrameSchedulerHealth) => void
}

type FrameProcessor = (capturedAt: number, signal: AbortSignal) => Promise<boolean>

export class FrameScheduler {
  private running = false
  private processing = false
  private callbackId: number | null = null
  private timerId: number | null = null
  private lastRunAt = -Infinity
  private startedAt: number | null = null
  private lastVideoFrameAt: number | null = null
  private lastSuccessfulAt: number | null = null
  private lastSuccessfulMediaTime: number | null = null
  private processingTask: Promise<boolean> | null = null
  private processingController: AbortController | null = null
  private processor: FrameProcessor | null = null
  private stallReported = false

  constructor(
    private readonly video: HTMLVideoElement,
    private intervalMs = 250,
    private readonly options: FrameSchedulerOptions = {},
  ) {}

  setInterval(intervalMs: number): void {
    this.intervalMs = Math.max(16, intervalMs)
  }

  start(processFrame: FrameProcessor): void {
    if (this.running) return
    this.running = true
    this.processor = processFrame
    this.startedAt = this.now()
    this.lastRunAt = -Infinity
    this.lastVideoFrameAt = null
    this.lastSuccessfulAt = null
    this.lastSuccessfulMediaTime = this.options.initialMediaTime !== null
      && this.options.initialMediaTime !== undefined
      && Number.isFinite(this.options.initialMediaTime)
      ? this.options.initialMediaTime
      : null
    this.stallReported = false
    this.schedule()
  }

  /** Drives capture from a visible companion window when video callbacks are throttled. */
  pulse(force = false): boolean {
    if (!this.running) return false
    const now = this.now()
    if (this.reportStallIfNeeded(now)) return false

    const videoCallbackReference = this.lastVideoFrameAt ?? this.startedAt
    if (!force
      && videoCallbackReference !== null
      && now - videoCallbackReference < this.externalFallbackDelayMs()) return false

    return this.tryProcess(now, this.video.currentTime)
  }

  health(at = this.now()): FrameSchedulerHealth {
    const reference = this.lastSuccessfulAt ?? this.startedAt
    return {
      running: this.running,
      processing: this.processing,
      stalled: Boolean(this.running && reference !== null && at - reference >= this.stallTimeoutMs()),
      startedAt: this.startedAt,
      lastVideoFrameAt: this.lastVideoFrameAt,
      lastSuccessfulAt: this.lastSuccessfulAt,
    }
  }

  stop(): void {
    this.running = false
    this.processor = null
    this.processingController?.abort()
    if (this.callbackId !== null) this.video.cancelVideoFrameCallback?.(this.callbackId)
    if (this.timerId !== null) window.clearTimeout(this.timerId)
    this.callbackId = null
    this.timerId = null
  }

  async waitForIdle(): Promise<void> {
    await this.processingTask
  }

  private schedule(): void {
    if (!this.running) return
    if (this.video.requestVideoFrameCallback) {
      this.callbackId = this.video.requestVideoFrameCallback((_now, metadata) => {
        this.callbackId = null
        if (!this.running) return
        const now = this.now()
        this.lastVideoFrameAt = now
        if (!this.reportStallIfNeeded(now)) this.tryProcess(now, metadata.mediaTime)
        this.schedule()
      })
      return
    }

    this.timerId = window.setTimeout(() => {
      this.timerId = null
      if (!this.running) return
      const now = this.now()
      if (!this.reportStallIfNeeded(now)) this.tryProcess(now, this.video.currentTime)
      this.schedule()
    }, Math.max(16, this.intervalMs / 2))
  }

  private tryProcess(now: number, mediaTime: number): boolean {
    const processor = this.processor
    if (!processor || this.processing || now - this.lastRunAt < this.intervalMs) return false
    if (this.lastSuccessfulMediaTime !== null
      && Number.isFinite(mediaTime)
      && mediaTime <= this.lastSuccessfulMediaTime + MEDIA_TIME_EPSILON) return false

    this.processing = true
    this.lastRunAt = now
    const controller = new AbortController()
    this.processingController = controller
    const task = Promise.resolve().then(() => processor(Date.now(), controller.signal))
    this.processingTask = task
    void task.then(
      (successful) => {
        if (!successful || controller.signal.aborted) return
        this.lastSuccessfulAt = this.now()
        if (Number.isFinite(mediaTime)) this.lastSuccessfulMediaTime = mediaTime
        this.stallReported = false
      },
      () => undefined,
    ).finally(() => {
      if (this.processingTask !== task) return
      this.processingTask = null
      this.processingController = null
      this.processing = false
    })
    return true
  }

  private reportStallIfNeeded(now: number): boolean {
    const health = this.health(now)
    if (!health.stalled) return false
    if (!this.stallReported) {
      this.stallReported = true
      this.options.onStalled?.(health)
    }
    return true
  }

  private externalFallbackDelayMs(): number {
    return Math.max(
      this.options.externalFallbackDelayMs ?? MIN_EXTERNAL_FALLBACK_DELAY_MS,
      this.intervalMs * 2,
    )
  }

  private stallTimeoutMs(): number {
    return Math.max(1_000, this.options.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS)
  }

  private now(): number {
    return this.options.now?.() ?? Date.now()
  }
}
